/**
 * WHERE AN ANALYTICS-ONLY USER MAY GO.
 *
 * An Analytics-only user is an active account with NEITHER the Manager box nor the Shop Floor
 * box ticked (`userIsAnalyticsOnly` in `utility/userRole.ts`). They do not consume one of the
 * site's seats — see `Licensing/SeatCapPolicy.cs` in the Function App — and in exchange the app
 * is closed to them except for **Analytics**, where they can open and run the saved reports that
 * have been **shared with them**.
 *
 * ⚠️ THIS IS A VIEW RULE, NOT A SECURITY BOUNDARY, AND THE DISTINCTION IS NOT ACADEMIC.
 * Every route guard in this repo is trivially bypassable — the user can edit their own
 * `sessionStorage` and React state. What actually protects the data is the BFF:
 *
 *   - `GET api/report/saved` returns a caller's OWN reports plus the ones explicitly shared
 *     with them, and nothing else. That is SQL (`SavedReportsRepository.GetVisible`) on top of
 *     the WHOTO row-level-security policy, not a client filter.
 *   - `POST api/report/saved` and `GET api/users` are manager-gated server-side
 *     (`RoleGuard.RequireManager`), so an Analytics-only user cannot save, share or enumerate
 *     colleagues whatever the UI shows them.
 *
 * So this module's job is to stop them landing on screens that are empty or meaningless for
 * them, not to be the thing that keeps them out. Do not add a rule here and treat the
 * corresponding server-side check as therefore unnecessary.
 */

/** The one screen an Analytics-only user is for. Capitalised — react-router v6 is case-sensitive. */
export const ANALYTICS_PATH = "/Analytics";

/**
 * Paths that stay open to everyone signed in, Analytics-only users included.
 *
 * `termsandconditions` and `consent-details` because an account that cannot accept the terms
 * cannot use anything; `/diagnostic` because reporting a problem must never depend on having
 * access to the thing that is broken. `/login` and `/logout` are `GuestLayout` routes and never
 * reach this check, but are listed so the set reads as the complete answer to "where may they
 * go".
 */
const ALWAYS_OPEN: readonly string[] = Object.freeze([
  ANALYTICS_PATH,
  "/termsandconditions",
  "/consent-details",
  "/diagnostic",
  "/login",
  "/logout",
]);

/** CORP/CORP2 only — the org picker. An Analytics-only corp user still has to pick an org. */
const CORP_ONLY = "/choose-organization";

export type AnalyticsOnlyDecision =
  /** Not an Analytics-only user, or they are already somewhere they may be. Render the screen. */
  | "allow"
  /** Send them to Analytics. */
  | "redirect"
  /**
   * They are Analytics-only and this environment has Analytics switched off, so there is
   * nowhere at all to send them. The caller shows a message; redirecting to a screen the
   * `AnalyticsRoute` guard would bounce straight back to `/home` is an infinite loop.
   */
  | "unavailable";

export interface AnalyticsOnlyContext {
  /** `userIsAnalyticsOnly(user)`. */
  readonly isAnalyticsOnly: boolean;
  /** `isAnalyticsEnabled()` — the `AnalyticsEnabled` flag for THIS environment. */
  readonly analyticsEnabled: boolean;
  /** `userIsCorp(user)`. */
  readonly isCorp: boolean;
  /** `location.pathname`. */
  readonly pathname: string;
}

/**
 * Leading slash on, trailing slash off, query and hash dropped — so `/Analytics/` and
 * `/Analytics?tab=x` are both `/Analytics`. Deliberately NOT lower-cased: the route is
 * `/Analytics` and react-router v6 is case-sensitive, so `/analytics` genuinely is a different
 * (non-existent) path and must not be treated as a match.
 */
const normalise = (pathname: string): string => {
  const withoutQuery = (pathname || "").split("?")[0].split("#")[0];
  const withLeading = withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
  return withLeading.length > 1 ? withLeading.replace(/\/+$/, "") : withLeading;
};

/** `/diagnostic` and `/diagnostic/anything`, but never `/diagnostics`. */
const matches = (path: string, allowed: string): boolean =>
  path === allowed || path.startsWith(`${allowed}/`);

export function analyticsOnlyDecision(context: AnalyticsOnlyContext): AnalyticsOnlyDecision {
  if (!context.isAnalyticsOnly) {
    return "allow";
  }

  // Checked BEFORE the allow-list, so an environment with Analytics switched off gives one
  // clear answer everywhere instead of an Analytics screen the route guard then bounces.
  if (!context.analyticsEnabled) {
    return "unavailable";
  }

  const path = normalise(context.pathname);

  if (ALWAYS_OPEN.some((allowed) => matches(path, allowed))) {
    return "allow";
  }

  if (context.isCorp && matches(path, CORP_ONLY)) {
    return "allow";
  }

  return "redirect";
}
